import { useNavigate } from 'react-router-dom';
import { Search, Menu, User, LogOut, ChevronDown, Building2, Check } from 'lucide-react';
import { signOut } from 'firebase/auth';
import { useFirebase } from '@mesterplan/shared-firebase';
import { useAuth } from '@mesterplan/auth-react';
import { useCompany } from '@/contexts/CompanyContext';
import { idpEnabled } from '@/lib/idp';
import { WhatsNewMenuDot } from '@/components/whats-new/WhatsNewSidebarRow';
import { NotificationBell } from '@/components/notifications/NotificationBell';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';

// Only rendered in IDP mode — safely calls useAuth() which requires OidcAuthProvider.
function IdpUserDropdown({ navigate, isPartnerUser }: { navigate: ReturnType<typeof useNavigate>; isPartnerUser: boolean }) {
  const { user, logout } = useAuth();
  const displayName = user?.displayName || user?.email || 'Bruger';
  const initial = displayName.charAt(0).toUpperCase();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <div className="flex items-center gap-3 cursor-pointer group">
          <div className="hidden sm:block text-right">
            <p className="text-[14px] font-semibold text-dark dark:text-dark-text group-hover:text-primary dark:group-hover:text-primary-400 transition-colors">
              {displayName}
            </p>
            <p className="text-[12px] text-muted dark:text-dark-muted">{user?.email}</p>
          </div>
          <div className="w-10 h-10 rounded-full bg-primary-50 dark:bg-primary-900/30 flex items-center justify-center ring-2 ring-bordergray dark:ring-dark-border group-hover:ring-primary/30 transition-all">
            <span className="text-primary dark:text-primary-400 font-semibold text-[14px]">{initial}</span>
          </div>
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuItem onClick={() => navigate(isPartnerUser ? '/profile/settings' : '/profile')}>
          <User className="h-4 w-4" />
          Min profil
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => void logout()} className="text-error hover:!bg-error-light dark:hover:!bg-error/20">
          <LogOut className="h-4 w-4" />
          Log ud
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function TopNavbar({ onOpenMobileMenu }: {
  onOpenMobileMenu?: () => void;
} = {}) {
  const navigate = useNavigate();
  const { auth, currentUser } = useFirebase();
  const { selectedCompany, companies, setSelectedCompany, isPartnerUser } = useCompany();
  // Get user's role from company membership
  const userMembership = selectedCompany?.userMemberships?.find(
    (m) => m.uid === currentUser?.uid
  );

  const getRoleLabel = (role?: string) => {
    switch (role) {
      case 'owner': return 'Ejer';
      case 'admin': return 'Administrator';
      case 'user': return 'Bruger';
      default: return '';
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <header className="sticky top-0 z-30 h-[70px] bg-white/80 dark:bg-dark-card/80 backdrop-blur-sm border-b border-bordergray dark:border-dark-border flex items-center justify-between px-6">
      {/* Left side */}
      <div className="flex items-center gap-4">
        <button aria-label="Åbn menu" onClick={onOpenMobileMenu} className="relative lg:hidden p-2 rounded-lg hover:bg-surface dark:hover:bg-dark-hover text-muted dark:text-dark-muted">
          <Menu className="h-5 w-5" />
          {idpEnabled && <WhatsNewMenuDot />}
        </button>

        <div className="hidden md:flex items-center gap-2.5 px-4 py-2.5 bg-surface dark:bg-dark-hover rounded-lg w-[280px] border border-transparent focus-within:border-primary focus-within:bg-white dark:focus-within:bg-dark-surface transition-all">
          <Search className="h-[18px] w-[18px] text-muted dark:text-dark-muted" />
          <input
            type="text"
            placeholder="Søg i projekter, kunder..."
            className="bg-transparent border-none outline-none text-[14px] text-dark dark:text-dark-text placeholder-muted dark:placeholder-dark-muted w-full"
          />
        </div>
      </div>

      {/* Right side */}
      <div className="flex items-center gap-3">
        {/* Company Switcher */}
        {selectedCompany && companies.length > 1 ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="hidden lg:flex items-center gap-2 px-3 py-1.5 bg-primary-50 dark:bg-primary-900/30 rounded-lg hover:bg-primary-100 dark:hover:bg-primary-900/50 transition-colors">
                <div className="w-2 h-2 rounded-full bg-success animate-pulse" />
                <span className="text-[13px] font-medium text-primary dark:text-primary-400">{selectedCompany.name}</span>
                <ChevronDown className="h-3.5 w-3.5 text-primary/60 dark:text-primary-400/60" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              {companies.map((company) => (
                <DropdownMenuItem
                  key={company.id}
                  onClick={() => setSelectedCompany(company)}
                  className="flex items-center gap-2"
                >
                  <Building2 className="h-4 w-4 text-muted dark:text-dark-muted" />
                  <span className="flex-1 truncate">{company.name}</span>
                  {company.relationship === 'supplier' && (
                    <span className="flex-shrink-0 rounded-full bg-primary-50 px-2 py-0.5 text-[11px] font-semibold text-primary dark:bg-primary-900/30 dark:text-primary-400">
                      via {companies.find((c) => c.id === company.supplierCompanyId)?.name ?? 'leverandør'}
                    </span>
                  )}
                  {company.id === selectedCompany.id && (
                    <Check className="h-4 w-4 text-primary" />
                  )}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : selectedCompany ? (
          <div className="hidden lg:flex items-center gap-2 px-3 py-1.5 bg-primary-50 dark:bg-primary-900/30 rounded-lg">
            <div className="w-2 h-2 rounded-full bg-success animate-pulse" />
            <span className="text-[13px] font-medium text-primary dark:text-primary-400">{selectedCompany.name}</span>
          </div>
        ) : null}

        {/* Notifications */}
        <NotificationBell />

        {/* Divider */}
        <div className="w-px h-8 bg-bordergray dark:bg-dark-border" />

        {/* User */}
        {idpEnabled ? (
          <IdpUserDropdown navigate={navigate} isPartnerUser={isPartnerUser} />
        ) : (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <div className="flex items-center gap-3 cursor-pointer group">
                <div className="hidden sm:block text-right">
                  <p className="text-[14px] font-semibold text-dark dark:text-dark-text group-hover:text-primary dark:group-hover:text-primary-400 transition-colors">
                    {currentUser?.displayName || 'Bruger'}
                  </p>
                  <p className="text-[12px] text-muted dark:text-dark-muted">{getRoleLabel(userMembership?.role)}</p>
                </div>
                <div className="w-10 h-10 rounded-full bg-primary-50 dark:bg-primary-900/30 flex items-center justify-center ring-2 ring-bordergray dark:ring-dark-border group-hover:ring-primary/30 transition-all">
                  <span className="text-primary dark:text-primary-400 font-semibold text-[14px]">
                    {currentUser?.displayName?.charAt(0) || currentUser?.email?.charAt(0).toUpperCase() || 'U'}
                  </span>
                </div>
              </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={() => navigate(isPartnerUser ? '/profile/settings' : '/profile')}>
                <User className="h-4 w-4" />
                Min profil
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-error hover:!bg-error-light dark:hover:!bg-error/20">
                <LogOut className="h-4 w-4" />
                Log ud
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </header>
  );
}
